import { useState, useEffect, useCallback } from 'react';
import RangosService from '../api/rangos.api';
import DeliveryService from '../../../services/delivery.service';

/**
 * Hook para orquestar datos y operaciones de rangos de precios (listado y
 * eliminación; alta/edición viven en RangoForm vía RangosService, ver
 * hooks/useRangoFormData.js).
 *
 * Combina RangosService (api/rangos.api.js, feature) con
 * DeliveryService.getTransports() (servicio legacy compartido con otros 4
 * consumidores, ver docs/api/rangos.md) para alimentar el listado y el
 * desplegable de transportistas del formulario. `transportistas` se expone
 * tal cual lo devuelve DeliveryService.getTransports() (objetos con CODIGO/
 * NOMBRE), sin mapear a { value, label }: esa transformación es presentación
 * (columnas del DataGrid, opciones del Select) y se hace donde se consuma.
 *
 * @returns {{
 *   rangos: Array,
 *   transportistas: Array,
 *   loading: boolean,
 *   error: { rangos: string|null, transportistas: string|null },
 *   reload: Function,
 *   eliminarRango: Function
 * }} Estado y operaciones de rangos de precios
 */
export const useRangos = () => {
    const [rangos, setRangos] = useState([]);
    const [transportistas, setTransportistas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState({ rangos: null, transportistas: null });

    // Carga (o recarga) solo la lista de rangos de precios. No usa el
    // 'loading' compartido: éste representa exclusivamente la carga inicial
    // en paralelo (ver useEffect de montaje); si tocara loading aquí, cada
    // llamada posterior (recarga manual) ocultaría el DataGrid entero en vez
    // de limitarse a refrescar sus filas.
    const fetchRangos = useCallback(async () => {
        try {
            const data = await RangosService.list();
            setRangos(data);
            setError((prev) => ({ ...prev, rangos: null }));
        } catch (err) {
            console.error('Error al cargar rangos de precios:', err);
            setError((prev) => ({ ...prev, rangos: err.message }));
        }
    }, []);

    // Carga la lista de transportistas (servicio legacy compartido)
    const fetchTransportistas = useCallback(async () => {
        try {
            const data = await DeliveryService.getTransports();
            setTransportistas(data);
            setError((prev) => ({ ...prev, transportistas: null }));
        } catch (err) {
            console.error('Error al cargar transportistas:', err);
            setError((prev) => ({ ...prev, transportistas: err.message }));
        }
    }, []);

    // Carga inicial en paralelo: Promise.allSettled asegura que el fallo de
    // una petición (p.ej. transportistas) no impida ver el resultado de la
    // otra (rangos), a diferencia de Promise.all, que cancelaría ambas
    useEffect(() => {
        (async () => {
            setLoading(true);
            await Promise.allSettled([fetchRangos(), fetchTransportistas()]);
            setLoading(false);
        })();
    }, [fetchRangos, fetchTransportistas]);

    /**
     * Recarga la lista de rangos de precios (no los transportistas, que no
     * cambian por operaciones CRUD de rangos). Se expone para que la página
     * pueda forzar una recarga manual.
     */
    const reload = useCallback(() => fetchRangos(), [fetchRangos]);

    // Elimina un rango; solo se quita del estado local si el backend confirma
    // éxito (si delete() lanza, el filter no se ejecuta y la fila permanece)
    const eliminarRango = useCallback(async (id) => {
        await RangosService.delete(id);
        setRangos((prev) => prev.filter((rango) => rango.id !== id));
    }, []);

    return {
        rangos,
        transportistas,
        loading,
        error,
        reload,
        eliminarRango,
    };
};
